/**
 * StreetDiceLobby — browse open Street Dice rooms + create one.
 *
 * MVP transport: REST polling every 3 s. Migrates to WebSockets in a future
 * session (see PRD backlog). We show a REAL-MONEY banner when applicable so
 * the player knows they're gambling with their actual wallet.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
    ArrowLeft, Dice5, Users, Bot, Plus, RefreshCw, Sparkles,
    Palette, Shield, AlertTriangle, HelpCircle, X, Lock,
} from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { getSessionTheme, shuffleTheme, ThemeOverlay } from './StreetDiceThemes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const HOUSE_RULES_KEY = 'street-dice-rules-seen-v1';

const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PHASE_LABEL = {
    waiting: 'Open — waiting for a shooter',
    announcing: 'Shooter announcing',
    covering: 'Fading the wager',
    rolling: 'Rolling',
    settling: 'Settling',
    hand_ended: 'Hand ended — next up',
};

export default function StreetDiceLobby() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [joinCodeOpen, setJoinCodeOpen] = useState(false);
    const [rulesOpen, setRulesOpen] = useState(false);
    const [theme, setTheme] = useState(() => getSessionTheme());

    // First-visit rules modal
    useEffect(() => {
        try {
            if (!localStorage.getItem(HOUSE_RULES_KEY)) {
                const t = setTimeout(() => setRulesOpen(true), 600);
                return () => clearTimeout(t);
            }
        } catch { /* ignore */ }
    }, []);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/street-dice/rooms`, { withCredentials: true });
            setRooms(data.rooms || []);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail) toast.error(detail);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 3000);
        return () => clearInterval(id);
    }, [load]);

    const openRooms = useMemo(
        () => rooms.filter((r) => (r.seats?.filter((s) => !s.is_bot).length || 0) < 8),
        [rooms],
    );

    return (
        <div className="a11y-font min-h-screen text-white relative overflow-hidden" style={{ background: theme.pageBg }} data-testid="street-dice-lobby">
            <ThemeOverlay theme={theme} />
            <Header />
            <div className="relative max-w-5xl mx-auto px-4 pt-6 pb-24">
                <button
                    onClick={() => nav('/lobby')}
                    className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-4"
                    data-testid="street-dice-back-link"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to lobby
                </button>

                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl overflow-hidden border p-6 sm:p-8 mb-6 relative"
                    style={{
                        borderColor: `${theme.accent}66`,
                        background: theme.circleBg,
                        boxShadow: `0 0 40px ${theme.glow}, inset 0 0 60px rgba(0,0,0,0.6)`,
                    }}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4" style={{ color: theme.accent }} />
                        <span
                            className="text-[10px] font-black tracking-[0.3em]"
                            style={{ color: theme.accent, fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                            data-testid="street-dice-theme-name"
                        >
                            STREET DICE · {theme.name.toUpperCase()}
                        </span>
                    </div>
                    <h1
                        className="text-4xl sm:text-6xl font-black text-white leading-tight"
                        style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                    >
                        RUN THE CIRCLE
                    </h1>
                    <p className="text-sm text-white/70 mt-2 max-w-xl">
                        {theme.tagline.toLowerCase()} · Up to 8 players · shooter vs. faders · real wallet (humans only) or practice · server-authoritative dice.
                    </p>

                    <div className="flex flex-wrap gap-2 mt-5">
                        <Button
                            onClick={() => setCreateOpen(true)}
                            className="font-black h-11 px-5 text-sm tracking-wider text-black"
                            style={{ background: theme.accent }}
                            data-testid="street-dice-create-btn"
                        >
                            <Plus className="w-4 h-4 mr-1" /> NEW ROOM
                        </Button>
                        <Button
                            onClick={() => setJoinCodeOpen(true)}
                            variant="outline"
                            className="h-11 px-4 border-white/20 text-white/80 hover:text-white hover:bg-white/5"
                            data-testid="street-dice-join-code-btn"
                        >
                            <Lock className="w-4 h-4 mr-1" /> Join by code
                        </Button>
                        <Button
                            onClick={() => setRulesOpen(true)}
                            variant="outline"
                            className="h-11 px-4 border-white/20 text-white/80 hover:text-white hover:bg-white/5"
                            data-testid="street-dice-rules-btn"
                        >
                            <HelpCircle className="w-4 h-4 mr-1" /> How to play
                        </Button>
                        <Button
                            onClick={() => setTheme(shuffleTheme(theme.id))}
                            variant="outline"
                            className="h-11 px-4 border-white/20 text-white/80 hover:text-white hover:bg-white/5"
                            data-testid="street-dice-shuffle-theme"
                            title="Shuffle visual theme"
                        >
                            <Palette className="w-4 h-4 mr-1" /> Shuffle vibe
                        </Button>
                        <Button
                            onClick={load}
                            variant="ghost"
                            className="h-11 px-3 text-white/60 hover:text-white"
                            data-testid="street-dice-refresh-btn"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </motion.div>

                {/* Room list */}
                <div className="flex items-center justify-between mb-3">
                    <h2
                        className="text-sm tracking-[0.28em] font-black text-white/80"
                        style={{ fontFamily: theme.headingFont }}
                    >
                        OPEN CIRCLES ({openRooms.length})
                    </h2>
                    <span className="text-[10px] font-mono text-white/40">Refreshing every 3s</span>
                </div>

                {loading ? (
                    <div className="text-white/50 text-sm py-8 text-center" data-testid="street-dice-loading">
                        Loading rooms…
                    </div>
                ) : openRooms.length === 0 ? (
                    <div
                        className="rounded-2xl border border-dashed p-8 text-center"
                        style={{ borderColor: `${theme.accent}44`, background: 'rgba(0,0,0,0.3)' }}
                        data-testid="street-dice-empty"
                    >
                        <Dice5 className="w-10 h-10 mx-auto mb-3" style={{ color: theme.accent }} />
                        <p className="text-white/80 font-bold">Nobody is on the corner yet.</p>
                        <p className="text-white/50 text-xs mt-1 mb-4">Start a new room and the bots will roll up.</p>
                        <Button
                            onClick={() => setCreateOpen(true)}
                            className="font-black text-black"
                            style={{ background: theme.accent }}
                            data-testid="street-dice-create-btn-empty"
                        >
                            <Plus className="w-4 h-4 mr-1" /> START A CIRCLE
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {openRooms.map((r) => (
                            <RoomCard
                                key={r.id}
                                room={r}
                                theme={theme}
                                onOpen={() => nav(`/casino/street-dice/${r.id}`)}
                                mine={r.seats?.some((s) => s.user_id === user?.id)}
                            />
                        ))}
                    </div>
                )}

                <div className="mt-6 flex items-start gap-2 rounded-xl border border-white/10 bg-black/40 p-3">
                    <Shield className="w-4 h-4 text-white/60 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-white/60 leading-snug">
                        Dice are rolled on our server using a cryptographic PRNG. Bots have distinct
                        personalities and can be replaced by a human seat any time. Real-money rooms
                        deduct from your BETohalics wallet — house holds no rake on the main wager.
                    </p>
                </div>
            </div>

            <CreateRoomDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                theme={theme}
                onCreated={(room) => {
                    setCreateOpen(false);
                    nav(`/casino/street-dice/${room.id}`);
                }}
            />
            <JoinByCodeDialog
                open={joinCodeOpen}
                onClose={() => setJoinCodeOpen(false)}
                theme={theme}
                onResolved={(room) => {
                    setJoinCodeOpen(false);
                    nav(`/casino/street-dice/${room.id}?code=${encodeURIComponent(room.join_code || '')}`);
                }}
            />
            <HouseRulesDialog
                open={rulesOpen}
                onClose={() => {
                    setRulesOpen(false);
                    try { localStorage.setItem(HOUSE_RULES_KEY, '1'); } catch { /* ignore */ }
                }}
                theme={theme}
            />
        </div>
    );
}

// ─── Join by code dialog ───────────────────────────────────────────────

const JoinByCodeDialog = ({ open, onClose, onResolved, theme }) => {
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) { setCode(''); setBusy(false); }
    }, [open]);

    const submit = async (e) => {
        e?.preventDefault?.();
        const c = code.trim().toUpperCase();
        if (c.length < 4) return toast.error('Enter the join code');
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/street-dice/rooms/lookup-by-code`,
                { join_code: c },
                { withCredentials: true },
            );
            // Attach the code so the room screen can auto-join with it.
            onResolved({ ...data, join_code: c });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Invalid code');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${theme.accent}55` }}>
                <DialogHeader>
                    <DialogTitle
                        className="text-white"
                        style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                    >
                        Join a private table
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <Label className="text-xs text-white/70">Join code</Label>
                    <Input
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="A1B2C3"
                        maxLength={12}
                        autoFocus
                        className="bg-black/40 border-white/10 text-white text-2xl h-14 font-black tracking-[0.4em] text-center"
                        data-testid="street-dice-join-code-input"
                    />
                    <div className="flex gap-2">
                        <Button type="button" variant="ghost" onClick={onClose} className="flex-1 text-white/70" disabled={busy}>Cancel</Button>
                        <Button
                            type="submit"
                            disabled={busy || !code.trim()}
                            className="flex-1 font-black text-black"
                            style={{ background: theme.accent }}
                            data-testid="street-dice-join-code-submit"
                        >
                            {busy ? 'Looking…' : 'FIND TABLE'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// ─── Room card ─────────────────────────────────────────────────────────

const RoomCard = ({ room, onOpen, theme, mine }) => {
    const humans = room.seats?.filter((s) => !s.is_bot).length || 0;
    const bots = room.seats?.filter((s) => s.is_bot).length || 0;
    const total = humans + bots;
    const isReal = room.mode === 'real';
    return (
        <button
            onClick={onOpen}
            className="text-left rounded-2xl border-2 p-4 transition hover:-translate-y-0.5 relative overflow-hidden"
            style={{
                borderColor: mine ? theme.accent : `${theme.accent}44`,
                background: theme.circleBg,
                boxShadow: mine ? `0 0 22px ${theme.glow}` : 'inset 0 0 30px rgba(0,0,0,0.5)',
            }}
            data-testid={`street-dice-room-card-${room.id}`}
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                    <div
                        className="text-lg font-black text-white truncate"
                        style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                    >
                        {room.name}
                    </div>
                    <div className="text-[11px] font-mono text-white/60 mt-0.5 truncate">
                        {PHASE_LABEL[room.phase] || room.phase}
                    </div>
                </div>
                <span
                    className={`px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest ${isReal ? 'text-black' : 'text-white/80'}`}
                    style={{
                        background: isReal ? theme.accent : 'rgba(255,255,255,0.1)',
                        border: isReal ? 'none' : '1px solid rgba(255,255,255,0.15)',
                    }}
                >
                    {isReal ? 'REAL $' : 'PRACTICE'}
                </span>
            </div>

            <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-3 text-xs text-white/70">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{humans}</span>
                    <span className="flex items-center gap-1"><Bot className="w-3.5 h-3.5" />{bots}</span>
                    <span className="text-white/40">·</span>
                    <span className="font-mono">{total}/6</span>
                </div>
                <div className="text-[10px] font-mono text-white/60">
                    Wager <span className="text-white">{fmt$(room.min_wager)}–{fmt$(room.max_wager)}</span>
                </div>
            </div>

            {mine && (
                <div
                    className="absolute top-2 right-2 text-[9px] font-black px-1.5 py-0.5 rounded"
                    style={{ background: theme.accent, color: '#000' }}
                >
                    SEATED
                </div>
            )}
        </button>
    );
};

// ─── Create room dialog ────────────────────────────────────────────────

const CreateRoomDialog = ({ open, onClose, onCreated, theme }) => {
    const { user } = useAuth();
    const playableWallet = Math.max(
        0,
        Number(user?.credits ?? 0) + Number(user?.bonus_credits ?? 0),
    );
    const [name, setName] = useState('');
    const [mode, setMode] = useState('real');
    const [buyIn, setBuyIn] = useState('100');
    const [minWager, setMinWager] = useState('5');
    const [maxWager, setMaxWager] = useState('50');
    const [fillWithBots, setFillWithBots] = useState(true);
    const [isPrivate, setIsPrivate] = useState(false);
    const [customCode, setCustomCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [realAllowed, setRealAllowed] = useState(false);

    // Check whether real-money mode is enabled by admin
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/street-dice/public-settings`, { withCredentials: true });
                setRealAllowed(!!data.real_wallet_enabled);
            } catch {
                setRealAllowed(false);
            }
        })();
    }, [open]);

    useEffect(() => {
        if (!open) {
            setName('');
            setMode('real');       // Default to REAL money — practice is the exception
            setBuyIn('100');
            setMinWager('5');
            setMaxWager('50');
            setFillWithBots(true);
            setIsPrivate(false);
            setCustomCode('');
            setBusy(false);
        }
    }, [open]);

    // Real-money mode: auto-fit the wager range to the player's actual wallet
    // so they can't accidentally set a $500 max on a $60 wallet. Practice mode
    // keeps the defaults since fun-chips are unlimited.
    useEffect(() => {
        if (!open || mode !== 'real') return;
        const walletCap = Math.max(1, Math.floor(playableWallet));
        // Default min = $5, but never above 20% of wallet.
        const suggestedMin = Math.max(1, Math.min(5, Math.floor(walletCap * 0.2) || 1));
        // Default max = $50 or the whole wallet, whichever is smaller.
        const suggestedMax = Math.max(suggestedMin, Math.min(50, walletCap));
        setMinWager(String(suggestedMin));
        setMaxWager(String(suggestedMax));
    }, [open, mode, playableWallet]);

    const submit = async () => {
        if (!name.trim()) return toast.error('Give the room a name');
        const minW = parseFloat(minWager);
        const maxW = parseFloat(maxWager);
        if (!minW || minW <= 0 || !maxW || maxW < minW) return toast.error('Fix wager range');
        // Real-money rooms are wallet-tied — buy-in is irrelevant. Send a wide
        // range derived from the wager span so backend validation is happy.
        // Practice rooms still take an actual buy-in for the fun-chip stack.
        let minBuyIn, maxBuyIn;
        if (mode === 'real') {
            minBuyIn = Math.max(1, Math.floor(minW));
            maxBuyIn = Math.max(minBuyIn + 1, Math.ceil(maxW * 20));
        } else {
            const bi = parseFloat(buyIn);
            if (!bi || bi <= 0) return toast.error('Enter a buy-in');
            minBuyIn = Math.max(1, Math.floor(bi / 2));
            maxBuyIn = Math.ceil(bi * 2);
        }
        // Optional custom code — creator-picked, 4–12 alphanumeric.
        let submitCode = null;
        if (isPrivate && customCode.trim()) {
            const c = customCode.trim().toUpperCase();
            if (!/^[A-Z0-9]{4,12}$/.test(c)) {
                return toast.error('Custom code must be 4–12 letters or numbers (no spaces or symbols)');
            }
            submitCode = c;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/street-dice/rooms`, {
                name: name.trim(),
                mode,
                min_buy_in: minBuyIn,
                max_buy_in: maxBuyIn,
                min_wager: minW,
                max_wager: maxW,
                fill_with_bots: fillWithBots,
                is_private: isPrivate,
                ...(submitCode ? { join_code: submitCode } : {}),
            }, { withCredentials: true });
            onCreated(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not create room');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${theme.accent}55` }}>
                <DialogHeader>
                    <DialogTitle
                        className="text-white"
                        style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                    >
                        Start a new circle
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs text-white/70">Room name</Label>
                        <Input
                            value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="12th & Main"
                            className="bg-black/40 border-white/10 text-white mt-1"
                            data-testid="street-dice-create-name"
                            maxLength={40}
                        />
                    </div>

                    <div>
                        <Label className="text-xs text-white/70">Mode</Label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                            <button
                                type="button"
                                onClick={() => setMode('practice')}
                                className={`rounded-lg border py-2 text-xs font-black tracking-widest ${mode === 'practice' ? 'text-black' : 'text-white/70'}`}
                                style={{
                                    background: mode === 'practice' ? theme.accent : 'transparent',
                                    borderColor: mode === 'practice' ? theme.accent : 'rgba(255,255,255,0.15)',
                                }}
                                data-testid="street-dice-create-mode-practice"
                            >
                                PRACTICE
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!realAllowed) {
                                        toast.error('Real-money rooms are locked. Ask an admin to enable them.');
                                        return;
                                    }
                                    setMode('real');
                                }}
                                className={`rounded-lg border py-2 text-xs font-black tracking-widest ${mode === 'real' ? 'text-black' : 'text-white/70'} ${!realAllowed ? 'opacity-50' : ''}`}
                                style={{
                                    background: mode === 'real' ? '#FFD700' : 'transparent',
                                    borderColor: mode === 'real' ? '#FFD700' : 'rgba(255,255,255,0.15)',
                                }}
                                data-testid="street-dice-create-mode-real"
                            >
                                REAL $
                            </button>
                        </div>
                        {mode === 'real' && (
                            <div className="mt-2 flex items-start gap-2 rounded-lg p-2 border border-[#FFD700]/40 bg-[#FFD700]/10">
                                <AlertTriangle className="w-4 h-4 text-[#FFD700] mt-0.5 shrink-0" />
                                <p className="text-[11px] text-white/85 leading-snug">
                                    Real money: every wager pulls straight from your BETohalics wallet — no buy-in, no room stack. Wins land back in your wallet immediately.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className={`grid ${mode === 'real' ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
                        {mode !== 'real' && (
                            <div>
                                <Label className="text-xs text-white/70">Buy-in</Label>
                                <Input
                                    type="number" min="1" value={buyIn}
                                    onChange={(e) => setBuyIn(e.target.value)}
                                    className="bg-black/40 border-white/10 text-white mt-1"
                                    data-testid="street-dice-create-buyin"
                                />
                            </div>
                        )}
                        <div>
                            <Label className="text-xs text-white/70">Min wager</Label>
                            <Input
                                type="number" min="1" value={minWager}
                                onChange={(e) => setMinWager(e.target.value)}
                                className="bg-black/40 border-white/10 text-white mt-1"
                                data-testid="street-dice-create-minwager"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-white/70">Max wager</Label>
                            <Input
                                type="number" min="1" value={maxWager}
                                onChange={(e) => setMaxWager(e.target.value)}
                                className="bg-black/40 border-white/10 text-white mt-1"
                                data-testid="street-dice-create-maxwager"
                            />
                        </div>
                    </div>
                    {mode === 'real' && (
                        <p className="text-[10px] text-white/50 -mt-1" data-testid="street-dice-wallet-hint">
                            Wallet: <span className="text-[#FFD700] font-bold">{fmt$(playableWallet)}</span>
                            {' · '}wagers auto-capped so you can play a full circle
                        </p>
                    )}

                    {mode !== 'real' && (
                        <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 p-3">
                            <div>
                                <p className="text-sm font-bold text-white">Fill with bots</p>
                                <p className="text-[11px] text-white/50">Regulars roll up to keep the circle warm</p>
                            </div>
                            <Switch
                                checked={fillWithBots}
                                onCheckedChange={setFillWithBots}
                                data-testid="street-dice-create-bots"
                            />
                        </div>
                    )}
                    {mode === 'real' && (
                        <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-black/30 p-3" data-testid="street-dice-real-humans-only">
                            <Users className="w-4 h-4 text-white/60 mt-0.5 shrink-0" />
                            <p className="text-[11px] text-white/60 leading-snug">
                                Real-money tables are <span className="text-white font-bold">humans only</span> — no bots. Share your table so friends can roll up.
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 p-3">
                        <div>
                            <p className="text-sm font-bold text-white">Private table</p>
                            <p className="text-[11px] text-white/50">Hidden from the lobby. Share a 6-digit join code with friends.</p>
                        </div>
                        <Switch
                            checked={isPrivate}
                            onCheckedChange={setIsPrivate}
                            data-testid="street-dice-create-private"
                        />
                    </div>

                    {/* Custom join code — only shows when Private is on.
                        Empty = auto-generate a random 6-char code. */}
                    {isPrivate && (
                        <div className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-bold text-white">Custom join code</p>
                                <span className="text-[10px] text-white/50">optional</span>
                            </div>
                            <Input
                                value={customCode}
                                onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                                placeholder="e.g. HOMIES24"
                                maxLength={12}
                                className="bg-black/50 border-white/10 text-white text-lg font-black tracking-[0.35em] text-center"
                                data-testid="street-dice-create-custom-code"
                            />
                            <p className="text-[11px] text-white/50">
                                4–12 letters/numbers. Leave blank and we&apos;ll generate one for you.
                            </p>
                        </div>
                    )}

                    <div className="flex gap-2 mt-3">
                        <Button variant="ghost" onClick={onClose} className="flex-1 text-white/70" disabled={busy}>Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={busy}
                            className="flex-1 font-black text-black"
                            style={{ background: theme.accent }}
                            data-testid="street-dice-create-submit"
                        >
                            {busy ? 'Creating…' : 'CREATE ROOM'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// ─── House rules dialog ────────────────────────────────────────────────

export const HouseRulesDialog = ({ open, onClose, theme }) => (
    <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${theme.accent}55` }}>
            <DialogHeader>
                <div className="flex items-center justify-between gap-2">
                    <DialogTitle
                        className="text-white"
                        style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                    >
                        House Rules — Street Dice
                    </DialogTitle>
                    <button onClick={onClose} className="text-white/50 hover:text-white" aria-label="Close" data-testid="street-dice-rules-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </DialogHeader>
            <div className="space-y-3 text-sm text-white/85 leading-snug">
                <RuleRow n="1" title="The shooter announces a wager" body="One player is the shooter. They announce how much they're rolling for (within room limits)." theme={theme} />
                <RuleRow n="2" title="The others fade it" body="Anyone else at the circle covers a piece of the wager. Bots auto-fade what humans don't." theme={theme} />
                <RuleRow n="3" title="Come-out roll" body="Roll a 7 or 11 — shooter wins. 2, 3, or 12 — shooter loses. Any other total sets the POINT." theme={theme} />
                <RuleRow n="4" title="Chase the point" body="Roll the point again to win. Roll a 7 first and everyone else beats the shooter." theme={theme} />
                <RuleRow n="5" title="Even money" body="Matched stakes pay 1:1. Uncovered stake is refunded — never at risk." theme={theme} />
                <RuleRow n="6" title="No house rake" body="This is street dice — house takes nothing from the main wager. Play with people, not a pit boss." theme={theme} />
            </div>
            <Button onClick={onClose} className="mt-3 font-black text-black w-full" style={{ background: theme.accent }} data-testid="street-dice-rules-got-it">
                Got it — let me roll
            </Button>
        </DialogContent>
    </Dialog>
);

const RuleRow = ({ n, title, body, theme }) => (
    <div className="flex items-start gap-3">
        <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black text-black shrink-0"
            style={{ background: theme.accent }}
        >
            {n}
        </div>
        <div>
            <p className="font-bold text-white">{title}</p>
            <p className="text-white/70 text-[13px]">{body}</p>
        </div>
    </div>
);
